import { PageLayout } from '@/components/feature/PageLayout';
import { useFadeUp } from '@/hooks/useFadeUp';
import { Wrench, Clock, Zap, Shield, Lock, ArrowDown, AlertTriangle, LinkIcon, Smartphone, LayoutGrid } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const seo = {
  title: 'Nos prestations — Réparation, Dépannage & Motorisation volets Charleroi',
  description: 'Découvrez toutes nos prestations : réparation de volets roulants, dépannage urgent, motorisation et entretien à Charleroi. Volet bloqué, moteur HS, sangle cassée — nous intervenons dans le Hainaut.',
  keywords: 'prestations volet roulant Charleroi, réparation volet roulant, dépannage volet, motorisation volet, entretien volet roulant Hainaut',
  canonicalUrl: '/nos-prestations/',
};

const panneIcons: Record<string, LucideIcon> = {
  lock: Lock,
  'arrow-down': ArrowDown,
  'alert-triangle': AlertTriangle,
  link: LinkIcon,
  smartphone: Smartphone,
  'layout-grid': LayoutGrid,
};

const pannes = [
  {
    id: 'volet-bloque',
    icon: 'lock',
    title: 'Volet bloqué',
    description: 'Votre volet roulant est complètement bloqué, impossible de le faire bouger ? Plusieurs causes possibles : moteur grillé, axe de rotation bloqué, tablier sorti des coulisses, ou encore un corps étranger dans le coffre. Nous diagnostiquons rapidement la cause et intervenons pour débloquer votre volet.',
  },
  {
    id: 'volet-ne-remonte-plus',
    icon: 'arrow-down',
    title: 'Volet qui ne remonte plus',
    description: 'Le volet descend correctement mais refuse de remonter. C\'est souvent un problème de fin de course du moteur, de condensateur défectueux, ou d\'enrouleur de sangle bloqué. Nous réparons ce type de panne très fréquent en une seule intervention.',
  },
  {
    id: 'moteur-hs',
    icon: 'alert-triangle',
    title: 'Moteur HS',
    description: 'Le moteur de votre volet roulant ne répond plus, fait un bruit anormal ou a complètement grillé. Nous remplaçons les moteurs tubulaires de toutes marques : Somfy, Simu, Nice, Bubendorff, Profalux, etc. Intervention rapide avec moteur de rechange adapté.',
  },
  {
    id: 'sangle-cassee',
    icon: 'link',
    title: 'Sangle ou manivelle cassée',
    description: 'La sangle de votre volet manuel est déchirée, effilochée, ou le mécanisme de manivelle est cassé. Nous remplaçons sangle, enrouleur et manivelle. Possibilité de profiter de l\'intervention pour passer à la motorisation.',
  },
  {
    id: 'telecommande-hs',
    icon: 'smartphone',
    title: 'Télécommande HS',
    description: 'Votre télécommande ne pilote plus le volet. Problème de pile, de programmation ou de récepteur radio ? Nous diagnostiquons et reprogrammons votre télécommande ou la remplaçons si nécessaire. Compatible Somfy, Simu, Nice, etc.',
  },
  {
    id: 'lames-abimees',
    icon: 'layout-grid',
    title: 'Lames ou tablier abîmés',
    description: 'Des lames de votre volet sont tordues, cassées ou le tablier est endommagé ? Nous remplaçons les lames défectueuses ou le tablier complet si nécessaire. Intervention précise pour retrouver un volet comme neuf.',
  },
];

function Hero() {
  return (
    <section className="relative min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
      <img
        src="/images/hero-nos-prestations.webp"
        alt="Prestations réparation volets roulants Charleroi"
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/85 to-secondary-500/70" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
            Nos prestations
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85">
            Réparation, dépannage, motorisation et entretien de volets roulants à Charleroi
          </p>
        </div>
      </div>
    </section>
  );
}

export default function NosPrestationsPage() {
  const ref1 = useFadeUp();
  const ref2 = useFadeUp();
  const ref3 = useFadeUp();

  return (
    <PageLayout seo={seo}>
      <Hero />

      {/* Services principaux */}
      <section className="py-14 md:py-20 bg-background-50">
        <div ref={ref1} className="fade-up max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-10">Ce que nous faisons</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {[
              { icon: Wrench, title: 'Réparation de volets roulants', desc: 'Toutes pannes sur volets manuels et électriques. Diagnostic gratuit, devis clair, réparation soignée. Nous réparons plutôt que de remplacer systématiquement.' },
              { icon: Clock, title: 'Dépannage urgent', desc: 'Volet coincé en position ouverte ou fermée ? Nous intervenons rapidement pour sécuriser votre habitation. Service de dépannage 7j/7 dans tout le Hainaut.' },
              { icon: Zap, title: 'Motorisation', desc: 'Transformez votre volet manuel en volet électrique. Installation de moteur tubulaire, télécommande et possibilité domotique. Toutes marques.' },
              { icon: Shield, title: 'Entretien et maintenance', desc: 'Nettoyage, lubrification, vérification des fixations. Un entretien régulier prévient les pannes et prolonge la durée de vie de vos volets.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="card border border-background-200">
                  <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent-500" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-700 text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Images */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
            <div className="rounded-2xl overflow-hidden h-56 md:h-72">
              <img
                src="/images/prestations-mecanisme.webp"
                alt="Mécanisme interne de volet roulant et moteur tubulaire"
                title="Réparation mécanisme volet roulant"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="rounded-2xl overflow-hidden h-56 md:h-72">
              <img
                src="/images/prestations-intervention.webp"
                alt="Intervention à domicile pour réparation de volet roulant"
                title="Dépannage volet roulant à domicile"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pannes détaillées */}
      <section className="py-14 md:py-20 bg-background-100">
        <div ref={ref2} className="fade-up max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-4">Types de pannes que nous réparons</h2>
          <p className="section-subtitle text-center mb-10">Chaque panne a sa solution. Découvrez comment nous intervenons.</p>

          <div className="space-y-8">
            {pannes.map((panne) => {
              const Icon = panneIcons[panne.icon];
              const isEven = pannes.indexOf(panne) % 2 === 1;
              return (
                <div
                  key={panne.id}
                  id={panne.id}
                  className={`flex flex-col ${isEven ? 'md:flex-row-reverse' : 'md:flex-row'} gap-6 md:gap-8 items-start card border border-background-200`}
                >
                  <div className="w-14 h-14 rounded-xl bg-accent-50 flex items-center justify-center shrink-0">
                    {Icon && <Icon className="w-6 h-6 text-accent-500" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-foreground-700 text-lg mb-2">{panne.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{panne.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-primary-500" ref={ref3}>
        <div className="fade-up max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
            Votre panne n'est pas listée ?
          </h2>
          <p className="text-base text-white/80 mb-6">
            Contactez-nous pour un diagnostic gratuit. Nous réparons tous types de pannes sur volets roulants.
          </p>
          <a href="tel:+3280020410" className="btn-primary text-base px-8 py-3.5 inline-flex items-center gap-2">
            0800 20 410
          </a>
        </div>
      </section>
    </PageLayout>
  );
}